import {
	ButtonUtility,
	DatePicker,
	FilterField,
	PageHeader,
	TableCard,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { FleeterIcon } from "@features/copilot/components";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { Share04, Zap } from "@untitledui/icons";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { ListingDataTable } from "@/features/tracking/components/listing-data-table";
import {
	RANKING_BY_DRIVER_COLUMNS,
	RANKING_BY_VEHICLE_COLUMNS,
	RANKING_SORT_BY_MAP,
} from "@/features/tracking/constants/ranking";
import { useRankingList } from "@/features/tracking/hooks/use-ranking-list";
import {
	type RankingMode,
	renderRankingCell,
	renderRankingDetailRow,
} from "@/features/tracking/utils/ranking-render";
import { RankingModeToggle } from "@/shared/components/ranking-mode-toggle";
import { DASHBOARD_SECTION_CARD_CLASS } from "../constants/dashboard-section-card.constants";
import type { DashboardRankingDateRange } from "../hooks/use-dashboard-ranking-period";
import { dateValueToCalendarDate, stringToDateValue } from "../utils";

const DASHBOARD_RANKING_PAGE_SIZE = 5;

function formatCalendarDate(date: CalendarDate): string {
	return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
}

export function DashboardRankingSection() {
	const [isDriver, setIsDriver] = useState(false);
	const [localCustomDateRange, setLocalCustomDateRange] =
		useState<DashboardRankingDateRange>(null);
	const [sortDescriptor, setSortDescriptor] = useState<
		SortDescriptor | undefined
	>();

	const currentYear = dayjs().year();

	const minDate = useMemo(
		() => new CalendarDate(currentYear, 1, 1),
		[currentYear]
	);
	const maxDate = useMemo(() => today(getLocalTimeZone()), []);

	const selectedRange = useMemo(() => {
		if (localCustomDateRange) {
			return {
				startDate: formatCalendarDate(localCustomDateRange.startDate),
				endDate: formatCalendarDate(localCustomDateRange.endDate),
			};
		}
		const now = dayjs();
		return {
			startDate: now.startOf("month").format("YYYY-MM-DD"),
			endDate: now.endOf("month").format("YYYY-MM-DD"),
		};
	}, [localCustomDateRange]);

	const sortBy = sortDescriptor
		? RANKING_SORT_BY_MAP[String(sortDescriptor.column)]
		: undefined;

	const { data, isLoading, isError, refetch } = useRankingList({
		startDate: selectedRange.startDate,
		endDate: selectedRange.endDate,
		isDriver,
		pageNumber: 1,
		pageSize: DASHBOARD_RANKING_PAGE_SIZE,
		...(sortBy && {
			sortBy,
			sortDirection:
				sortDescriptor?.direction === "descending"
					? ("desc" as const)
					: ("asc" as const),
		}),
	});

	const items = data?.items ?? [];
	const mode: RankingMode = isDriver ? "driver" : "vehicle";
	const columns = isDriver
		? RANKING_BY_DRIVER_COLUMNS
		: RANKING_BY_VEHICLE_COLUMNS;

	return (
		<section aria-labelledby="dashboard-ranking-heading" className="mb-6">
			<div
				className={`${DASHBOARD_SECTION_CARD_CLASS} flex flex-col gap-6 p-5 md:p-6`}
			>
				<PageHeader
					actions={
						<ButtonUtility
							color="secondary"
							href="/tracking/ranking"
							icon={Share04}
							size="sm"
							tooltip="Abrir página de ranking"
						/>
					}
					subtitle=""
					title={
						<div className="flex gap-3">
							<div
								aria-hidden
								className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FEF0C7]"
							>
								<Zap className="size-5 text-[#B54708]" />
							</div>
							<div className="flex flex-col">
								<span id="dashboard-ranking-heading">Ranking</span>
								<span className="text-secondary text-sm leading-5">
									Desempenhos mais críticos
								</span>
							</div>
						</div>
					}
					titleAs="h2"
				/>

				<div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
					<FilterField label="Data inicial">
						<DatePicker
							aria-label="Data inicial"
							className="w-full"
							maxValue={maxDate}
							onApply={(date) => {
								if (!date) {
									return;
								}
								setLocalCustomDateRange({
									startDate: dateValueToCalendarDate(date),
									endDate: stringToDateValue(selectedRange.endDate) ?? maxDate,
								});
							}}
							size="sm"
							value={stringToDateValue(selectedRange.startDate)}
						/>
					</FilterField>

					<FilterField label="Data final">
						<DatePicker
							aria-label="Data final"
							className="w-full"
							displayFormatDdMmYyyy
							maxValue={maxDate}
							minValue={minDate}
							onApply={(date) => {
								if (!date) {
									return;
								}
								setLocalCustomDateRange({
									startDate:
										stringToDateValue(selectedRange.startDate) ?? minDate,
									endDate: dateValueToCalendarDate(date),
								});
							}}
							size="sm"
							value={stringToDateValue(selectedRange.endDate)}
						/>
					</FilterField>
				</div>

				<div className="flex w-full flex-wrap items-center justify-between gap-3">
					<RankingModeToggle isDriver={isDriver} onModeChange={setIsDriver} />
					<Button
						color="secondary"
						href="/copilot/summary"
						iconLeading={
							<span aria-hidden className="flex shrink-0">
								<FleeterIcon className="text-[#0080FE]" size={14} />
							</span>
						}
						size="sm"
					>
						Sugestões do Copiloto
					</Button>
				</div>

				<TableCard.Root variant="listing">
					<ListingDataTable
						ariaLabel={
							isDriver ? "Ranking de motoristas" : "Ranking de veículos"
						}
						columns={columns}
						density="compact"
						emptyContent="Nenhum registro de ranking neste período."
						errorMessage="Não foi possível carregar o ranking."
						expandable
						getRowKey={(item, index) =>
							isDriver
								? `${item.cpf ?? ""}|${item.name ?? ""}|${index}`
								: `${item.plate ?? ""}|${index}`
						}
						isError={isError}
						isLoading={isLoading}
						items={items}
						loadingLabel="Carregando ranking"
						onRetry={() => {
							refetch();
						}}
						onSortChange={setSortDescriptor}
						renderCell={(item, col, colIndex) =>
							renderRankingCell(item, col, colIndex, mode)
						}
						renderExpandedRow={renderRankingDetailRow}
						sortDescriptor={sortDescriptor}
						variant="listing"
					/>
				</TableCard.Root>
			</div>
		</section>
	);
}
